import { useCallback, useState } from "react";
import _ from "lodash";

import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import { MenuItem } from "components/Menu";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";

import { proformaSnapshotCreate } from "entities/Proforma/sdk";

export interface ICreateNamedVersionMenuItem {
  dealId: number;
  hasNamedVersion: boolean;
  onCreateNamedVersion: () => void;
}

const CreateNamedVersionMenuItem = ({
  dealId,
  hasNamedVersion,
  onCreateNamedVersion,
}: ICreateNamedVersionMenuItem) => {
  const [isOpen, setIsOpen] = useState(false);
  const [namedVersionDescription, setNamedVersionDescription] = useState("");

  const handleChangeDescription = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setNamedVersionDescription(event.target.value),
    [setNamedVersionDescription]
  );

  const handleCreateNamedVersion = useCallback(
    () =>
      proformaSnapshotCreate({ dealId, description: namedVersionDescription })
        .then(() => {
          toast.successMessage("Named version created successfully");
          onCreateNamedVersion();
          setIsOpen(false);
          setNamedVersionDescription("");
        })
        .catch(handleInvalidRequest),
    [dealId, namedVersionDescription, onCreateNamedVersion]
  );

  const handleOpen = useCallback(() => setIsOpen(true), []);

  const handleClose = useCallback(() => {
    setNamedVersionDescription("");
    setIsOpen(false);
  }, []);

  return (
    <>
      <Tooltip
        arrow
        placement="top"
        title={
          hasNamedVersion
            ? "A named version for this proforma already exists."
            : ""
        }
      >
        <span>
          <MenuItem
            disabled={hasNamedVersion}
            onClick={handleOpen}
            dataTestid="proforma-action-option"
          >
            <Text variant="text2" data-testid="action-option-label">
              Name this pro forma version
            </Text>
          </MenuItem>
        </span>
      </Tooltip>
      <Dialog open={isOpen} maxWidth="md" fullWidth onClose={handleClose}>
        <DialogTitle
          sx={{
            margin: 2,
            borderRadius: "8px",
            backgroundColor: colors.blue10,
          }}
        >
          <Text variant="h2" marginBottom={0.5} color={colors.gray100}>
            Create named version
          </Text>
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter a name or description for this pro forma version"
            defaultValue={namedVersionDescription}
            onChange={_.debounce(handleChangeDescription, 200)}
          />
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleCreateNamedVersion}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateNamedVersionMenuItem;
